import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import styles from "./Currency.module.scss";
import Button from "../../components/common/button/Button";
import PlusIcon from "../../assets/images/actions/PlusIcon";
import CurrencyService from "../../api/services/currency.service";
import Delete from "../../assets/images/actions/Delete";
import { toast } from "sonner";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import Loading from "../../components/common/loading/Loading";

interface Errors {
  name?: string;
  value?: string;
}

interface CurrencyTypes {
  id: number;
  name: string;
  value: number;
}

const Currency = () => {
  const { t } = useTranslation("translation");
  const { setButtons } = useAuth();
  const [currency, setCurrency] = useState<CurrencyTypes[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", value: "" });
  const [errors, setErrors] = useState<Partial<Errors>>({});
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const getData = async () => {
    setLoading(true);
    try {
      const res = await CurrencyService.getAllData();
      setCurrency(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<Errors> = {};
    if (!formData.name.trim())
      newErrors.name = t("currency.errors.name_required") as string;
    if (!formData.value.trim())
      newErrors.value = t("currency.errors.value_required") as string;
    else if (isNaN(parseFloat(formData.value)))
      newErrors.value = t("currency.errors.value_must_be_number") as string;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;
    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("value", parseFloat(formData.value).toString());
      await CurrencyService.createOne(data);
      toast.success(t("currency.messages.added_successfully"));
      setFormData({ name: "", value: "" });
      setIsVisible(false);
      getData();
    } catch (error) {
      console.error(error);
      toast.error(t("currency.messages.failed_to_add"));
    }
  };

  const handleInlineEdit = async (
    id: number,
    field: "name" | "value",
    newValue: string,
  ) => {
    try {
      const updated = currency.find((c) => c.id === id);
      if (!updated) return;

      const data = new FormData();
      data.append("name", field === "name" ? newValue : updated.name);
      data.append(
        "value",
        field === "value"
          ? parseFloat(newValue).toString()
          : updated.value.toString(),
      );

      await CurrencyService.updateData(id, data);
      toast.success(t("currency.messages.updated_successfully"));
      getData();
    } catch (error) {
      console.error(error);
      toast.error(t("currency.messages.update_failed"));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await CurrencyService.deleteData(id);
      toast.success(t("currency.messages.deleted_successfully"));
      getData();
      setDeleteModal(false);
    } catch (error) {
      toast.error(t("currency.messages.failed_to_delete"));
    }
  };

  useEffect(() => {
    setButtons([
      <Button
        icon={<PlusIcon />}
        key="add"
        onClick={() => {
          setIsVisible(true);
          setFormData({ name: "", value: "" });
          setErrors({});
        }}
      >
        {t("currency.add_button")}
      </Button>,
    ]);
    return () => setButtons([]);
  }, [setButtons]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.currency}>
      {isVisible && (
        <div className={styles.currency_item}>
          <div className={styles.box}>
            <div className={styles.input_control}>
              <input
                className={`${styles.input} ${
                  errors.name ? styles.input_error : ""
                }`}
                type="text"
                placeholder={t("currency.placeholders.enter_name") as string}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              {errors.name && <p className={styles.error}>{errors.name}</p>}
            </div>
          </div>
          <div className={styles.line} />
          <div className={styles.box}>
            <div className={styles.input_control}>
              <input
                className={`${styles.input} ${
                  errors.value ? styles.input_error : ""
                }`}
                type="number"
                step="0.01"
                placeholder={t("currency.placeholders.enter_value") as string}
                value={formData.value}
                onChange={(e) =>
                  setFormData({ ...formData, value: e.target.value })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAdd();
                  }
                }}
              />
              {errors.value && <p className={styles.error}>{errors.value}</p>}
            </div>
          </div>
        </div>
      )}
      {currency.map((el) => (
        <div className={styles.currency_item} key={el.id}>
          <div className={styles.box}>
            <div>
              <input
                className={styles.input}
                type="text"
                defaultValue={el.name}
                onBlur={(e) => {
                  const newValue = e.target.value.trim();
                  if (newValue && newValue !== el.name)
                    handleInlineEdit(el.id, "name", newValue);
                }}
              />
            </div>
          </div>
          <div className={styles.line} />
          <div className={styles.box}>
            <div>
              <input
                className={styles.input}
                type="number"
                step="0.01"
                defaultValue={el.value}
                onBlur={(e) => {
                  const newValue = parseFloat(e.target.value);
                  if (!isNaN(newValue) && newValue !== el.value)
                    handleInlineEdit(el.id, "value", newValue.toString());
                }}
              />
            </div>
          </div>
          <div className={styles.icons}>
            <button
              onClick={() => {
                setDeleteId(el.id);
                setDeleteModal(true);
              }}
            >
              <Delete />
            </button>
          </div>
        </div>
      ))}
      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("currency.delete_title") as string}
      />
    </div>
  );
};

export default Currency;
